import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommentReactionListComponent } from './comment-reaction-list/comment-reaction-list.component';
import { ViewFileComponent } from '../../../../../common/components/view-file/view-file.component';
import { DeleteConfirmationPopupComponent } from '../../../../../common/components/delete-confirmation-popup/delete-confirmation-popup.component';
import { ReportService } from '../../../../../common/services/report/report.service';
import { ActionList } from '../../../../../common/models/action-list.model';
import { LikeIconComponent } from '@kuiper/icons';
import {
  EmojiLookup,
  UserReaction,
  DiscussionComment,
} from '../request-discussion.model';
import { Meta } from '../../../../../common/models/api-response.model';
import { AppStorage } from '../../../../../common/enums/app-storage';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { SanitizeHtmlPipe } from '../../../../../../ui-components/pipes/sanitize-html/sanitize-html.pipe';
import { CookieService } from '../../../../../common/services/cookie/cookie.service';

@Component({
  selector: 'lib-discussion-comment-view',
  imports: [
    AvatarComponent,
    DatePipe,
    ActionListComponent,
    TranslocoPipe,
    LikeIconComponent,
    ViewFileComponent,
    CommentReactionListComponent,
    DeleteConfirmationPopupComponent,
    SanitizeHtmlPipe,
  ],
  templateUrl: './discussion-comment-view.component.html',
  styleUrl: './discussion-comment-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DiscussionCommentViewComponent {
  private readonly reportService = inject(ReportService);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);
  private readonly cookieService = inject(CookieService);

  deleteCommentModal = viewChild<TemplateRef<unknown>>('deleteCommentModal');

  comment = input.required<DiscussionComment>();
  reactionList = input.required<EmojiLookup[]>();
  isFirst = input<boolean>(false);
  hasLength = input<boolean>(false);
  isLast = input<boolean>(false);
  isDeleteCommentLoading = input<boolean>(false);
  commentReactions = input<UserReaction[]>([]);
  commentReactionsMeta = input<Meta>({
    current_page: 1,
    from: 0,
    last_page: 0,
    links: [],
    path: '',
    per_page: 0,
    to: 0,
    total: 0,
  });
  showAllReactions = input<boolean>(false);
  isReactionsLoading = input<boolean>(false);
  showAttachmentsOnly = input<boolean>(false);

  updateComment = output<DiscussionComment>();
  deleteComment = output<DiscussionComment>();
  storeReact = output<{ comment: DiscussionComment; slug: string }>();
  deleteReact = output<DiscussionComment>();
  toggleUserReactions = output<{
    showAllReactions: boolean;
    comment: DiscussionComment;
  }>();
  getCommentReactions = output<{
    comment: DiscussionComment;
    emojiSlug: string;
  }>();
  toggleEmojiTab = output<{ comment: DiscussionComment; emojiSlug: string }>();
  turnOffOutSideClick = output<boolean>();

  defaultTruncateLength = 300;

  isTruncated = signal<boolean>(true);
  showReactionList = signal<boolean>(false);
  react = signal<EmojiLookup | null>(null);
  emojiSizeInPixels = computed(() =>
    this.reportService.getCurrentValueInPixels(24 / 16)
  );
  user = signal(
    JSON.parse(this.cookieService.get(AppStorage.centralUser) || '{}')
  );
  editAction: ActionList = {
    key: 'edit',
    hasIcon: true,
    label: this.transloco.translate('edit_action_item'),
    hasDivider: false,
    labelCustomStyle: '',
    hasToggle: false,
    toggleValue: false,
  };
  deleteAction: ActionList = {
    key: 'delete',
    hasIcon: true,
    label: this.transloco.translate('delete_action_item'),
    hasDivider: false,
    labelCustomStyle: 'text-destructive-500 font-medium text-xs',
    hasToggle: false,
    toggleValue: false,
  };
  actionMenuItems = signal<ActionList[]>([this.editAction, this.deleteAction]);

  addEmoji(reaction: EmojiLookup) {
    if (this.comment().user_reaction?.emoji.id === reaction.id) {
      this.removeReactLocally(reaction);
      this.deleteReact.emit(this.comment());
    } else {
      this.addReactLocally(reaction);
      this.storeReact.emit({ comment: this.comment(), slug: reaction.value });
    }
  }

  removeReactLocally(reaction: EmojiLookup) {
    this.comment().user_reaction = null;
    this.react.set(null);
    const reactions = this.comment().reactions || [];
    if (reactions?.length) {
      const newReactions = reactions.filter(r => r.emoji.id !== reaction.id);
      if (newReactions?.length) {
        this.comment().reactions = newReactions;
      }
    }
  }

  addReactLocally(reaction: EmojiLookup) {
    this.react.set(reaction);
    const reactions = this.comment().reactions || [];
    if (reactions?.length) {
      reactions.push({
        id: Math.random() * 1000000,
        emoji: {
          id: reaction.id,
          name: reaction.label,
          slug: reaction.value,
          emoji: reaction.emoji,
        },
        user: this.user(),
      });
    }
  }

  handleAction($event: string) {
    if ($event === 'edit') {
      this.updateComment.emit(this.comment());
    } else if ($event === 'delete') {
      this.openModal();
    } else return;
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.deleteCommentModal(),
      width: '30',
    };

    this.modalService.modalContent.set(modal);
    this.turnOffOutSideClick.emit(true);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
    this.turnOffOutSideClick.emit(false);
  }

  confirmDelete() {
    this.deleteComment.emit(this.comment());
    this.closeModal();
  }
}
